import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import playbookService from '../services/playbookService';
import atomicService from '../services/atomicService';
import MitreTechniqueSelector from '../components/mitre/MitreTechniqueSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';

function PlaybookBuilder() {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const { playbookId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!playbookId;

  const [playbook, setPlaybook] = useState(playbookService.createEmptyPlaybook());
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Modal states
  const [showMitreSelector, setShowMitreSelector] = useState(false);
  const [showAtomicBrowser, setShowAtomicBrowser] = useState(false);
  const [atomicTests, setAtomicTests] = useState([]);
  const [atomicSearch, setAtomicSearch] = useState('');
  const [atomicLoading, setAtomicLoading] = useState(false);
  const [atomicFilterTechnique, setAtomicFilterTechnique] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isEditing) {
          const playbookData = await playbookService.getPlaybook(playbookId);
          setPlaybook(playbookData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [playbookId, isEditing]);

  const handleSave = async () => {
    try {
      setSaving(true);
      if (isEditing) {
        await playbookService.updatePlaybook(playbookId, playbook);
      } else {
        await playbookService.createPlaybook(playbook);
      }
      navigate('/playbooks');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const newOrder = playbook.steps.length + 1;
    setPlaybook({
      ...playbook,
      steps: [...playbook.steps, playbookService.createEmptyStep(newOrder)]
    });
    setActiveStep(playbook.steps.length);
  };

  const removeStep = (index) => {
    const newSteps = playbook.steps.filter((_, i) => i !== index);
    newSteps.forEach((step, i) => {
      step.order = i + 1;
    });
    setPlaybook({ ...playbook, steps: newSteps });
    if (activeStep >= newSteps.length) {
      setActiveStep(Math.max(0, newSteps.length - 1));
    }
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...playbook.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setPlaybook({ ...playbook, steps: newSteps });
  };

  const updateStepMultiple = (index, fields) => {
    const newSteps = [...playbook.steps];
    newSteps[index] = { ...newSteps[index], ...fields };
    setPlaybook({ ...playbook, steps: newSteps });
  };

  const moveStep = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === playbook.steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...playbook.steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + direction];
    newSteps[index + direction] = temp;

    newSteps.forEach((step, i) => {
      step.order = i + 1;
    });

    setPlaybook({ ...playbook, steps: newSteps });
    setActiveStep(index + direction);
  };

  const duplicateStep = (index) => {
    const stepToCopy = { ...playbook.steps[index] };
    stepToCopy.order = playbook.steps.length + 1;
    stepToCopy.name = `${stepToCopy.name} (Copy)`;
    setPlaybook({
      ...playbook,
      steps: [...playbook.steps, stepToCopy]
    });
    setActiveStep(playbook.steps.length);
  };

  // MITRE technique selection handler
  const handleMitreSelect = (technique) => {
    updateStepMultiple(activeStep, {
      techniqueId: technique.id,
      techniqueName: technique.name,
      tactic: technique.tactic
    });
    setShowMitreSelector(false);
  };

  // Atomic test browser
  const openAtomicBrowser = useCallback(async () => {
    setShowAtomicBrowser(true);
    setAtomicLoading(true);
    try {
      const step = playbook.steps[activeStep];
      // If step already has a technique, filter by it
      const filters = { limit: 500 };
      if (step?.techniqueId) {
        filters.techniqueId = step.techniqueId;
      }
      const data = await atomicService.getAtomicTests(filters);
      const testsArray = Array.isArray(data) ? data : (data?.tests || []);
      setAtomicTests(testsArray);
      setAtomicFilterTechnique(step?.techniqueId || '');
    } catch (err) {
      console.error('Error loading atomic tests:', err);
    } finally {
      setAtomicLoading(false);
    }
  }, [activeStep, playbook.steps]);

  const searchAtomicTests = useCallback(async (query, techniqueFilter) => {
    setAtomicLoading(true);
    try {
      const filters = { limit: 500 };
      if (query) filters.search = query;
      if (techniqueFilter) filters.techniqueId = techniqueFilter;
      const data = await atomicService.getAtomicTests(filters);
      const testsArray = Array.isArray(data) ? data : (data?.tests || []);
      setAtomicTests(testsArray);
    } catch (err) {
      console.error('Error searching atomic tests:', err);
    } finally {
      setAtomicLoading(false);
    }
  }, []);

  // Link an atomic test sub-test to the current step
  const handleLinkAtomicTest = (atomicTest, subTest) => {
    // Map executor type
    let executorType = 'shell';
    const execName = (subTest.executor?.type || subTest.executor?.name || '').toLowerCase();
    if (execName.includes('powershell')) executorType = 'powershell';
    else if (execName === 'cmd' || execName.includes('command_prompt')) executorType = 'cmd';
    else if (execName.includes('bash')) executorType = 'bash';
    else if (execName.includes('sh')) executorType = 'sh';
    else if (execName.includes('python')) executorType = 'python';

    // Map platforms
    const platforms = (subTest.supportedPlatforms || atomicTest.platforms || ['all']).map(p =>
      p === 'macos' ? 'darwin' : p
    );

    updateStepMultiple(activeStep, {
      atomicTestId: atomicTest._id,
      atomicTestName: subTest.name || atomicTest.name,
      atomicTestGuid: subTest.autoGeneratedGuid || '',
      name: playbook.steps[activeStep].name || subTest.name || atomicTest.name,
      description: playbook.steps[activeStep].description || subTest.description || atomicTest.description || '',
      techniqueId: atomicTest.techniqueId,
      techniqueName: atomicTest.name,
      tactic: atomicTest.tactic || '',
      command: subTest.executor?.command || atomicTest.executorCommand || '',
      cleanupCommand: subTest.executor?.cleanupCommand || atomicTest.cleanupCommand || '',
      executorType,
      platforms,
      requiresAdmin: subTest.executor?.elevationRequired || false
    });
    setShowAtomicBrowser(false);
  };

  const unlinkAtomicTest = () => {
    updateStepMultiple(activeStep, {
      atomicTestId: null,
      atomicTestName: '',
      atomicTestGuid: ''
    });
  };

  const currentStep = playbook.steps?.[activeStep];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <PageHeader
        title={isEditing ? t('playbooks.editPlaybook') : t('playbooks.createPlaybook')}
        subtitle={t('playbooks.buildSequences')}
      >
        <button
          onClick={() => navigate('/playbooks')}
          className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {t('common.saving')}
            </>
          ) : (
            t('playbooks.savePlaybook')
          )}
        </button>
      </PageHeader>

      {error && (
        <div className={`px-4 py-3 rounded mb-4 ${darkMode ? 'bg-red-900/50 border border-red-500 text-red-200' : 'bg-red-100 border border-red-400 text-red-700'}`}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Playbook Info */}
        <div className="lg:col-span-1 space-y-4">
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
            <h2 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('playbooks.playbookDetails')}</h2>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('common.name')} *</label>
                <input
                  type="text"
                  value={playbook.name}
                  onChange={(e) => setPlaybook({ ...playbook, name: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  placeholder="Credential Dumping Attack Chain"
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('common.description')}</label>
                <textarea
                  value={playbook.description}
                  onChange={(e) => setPlaybook({ ...playbook, description: e.target.value })}
                  className={`w-full px-3 py-2 rounded h-24 border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  placeholder="Describe what this playbook does..."
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.category')}</label>
                <select
                  value={playbook.category}
                  onChange={(e) => setPlaybook({ ...playbook, category: e.target.value })}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                >
                  {playbookService.PLAYBOOK_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.targetPlatforms')}</label>
                <div className="flex flex-wrap gap-2">
                  {playbookService.PLATFORMS.map((platform) => (
                    <label key={platform.value} className={`flex items-center gap-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={playbook.targetPlatforms?.includes(platform.value)}
                        onChange={(e) => {
                          const platforms = e.target.checked
                            ? [...(playbook.targetPlatforms || []), platform.value]
                            : playbook.targetPlatforms?.filter(p => p !== platform.value);
                          setPlaybook({ ...playbook, targetPlatforms: platforms });
                        }}
                        className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                      />
                      {platform.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.tags')}</label>
                <input
                  type="text"
                  value={playbook.tags?.join(', ') || ''}
                  onChange={(e) => setPlaybook({
                    ...playbook,
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                  })}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  placeholder="apt, mimikatz, credential-theft"
                />
              </div>

              <div className="flex gap-4">
                <label className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <input
                    type="checkbox"
                    checked={playbook.isPublic}
                    onChange={(e) => setPlaybook({ ...playbook, isPublic: e.target.checked })}
                    className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                  />
                  {t('playbooks.public')}
                </label>
                <label className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <input
                    type="checkbox"
                    checked={playbook.isTemplate}
                    onChange={(e) => setPlaybook({ ...playbook, isTemplate: e.target.checked })}
                    className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                  />
                  {t('playbooks.template')}
                </label>
              </div>
            </div>
          </div>

          {/* Steps List */}
          <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('playbooks.steps')} ({playbook.steps?.length || 0})</h2>
              <button
                onClick={addStep}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
              >
                + {t('playbooks.addStep')}
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {playbook.steps?.map((step, index) => (
                <div
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className={`p-3 rounded cursor-pointer ${
                    activeStep === index
                      ? darkMode ? 'bg-red-900/50 border border-red-500' : 'bg-red-100 border border-red-400'
                      : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm flex-shrink-0">{step.order}.</span>
                        <span className={`font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {step.name || t('playbooks.unnamedStep')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {step.techniqueId && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                            {step.techniqueId}
                          </span>
                        )}
                        {step.atomicTestId && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                            Atomic
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs flex-shrink-0 ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{step.executorType}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Step Editor */}
        <div className="lg:col-span-2">
          {currentStep ? (
            <div className={`rounded-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Step {currentStep.order}: {currentStep.name || 'Unnamed'}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => moveStep(activeStep, -1)}
                    disabled={activeStep === 0}
                    className={`p-2 disabled:opacity-50 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    title="Move Up"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveStep(activeStep, 1)}
                    disabled={activeStep === playbook.steps.length - 1}
                    className={`p-2 disabled:opacity-50 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    title="Move Down"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => duplicateStep(activeStep)}
                    className={`p-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                    title="Duplicate"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                      <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeStep(activeStep)}
                    disabled={playbook.steps.length === 1}
                    className="p-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Linked Atomic Test Banner */}
              {currentStep.atomicTestId && (
                <div className={`mb-4 p-3 rounded-lg border ${darkMode ? 'bg-orange-900/20 border-orange-700' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-lg`}>&#9889;</span>
                      <div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                          {t('playbooks.linkedAtomicTest')}
                        </span>
                        <p className={`text-xs ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                          {currentStep.atomicTestName}
                          {currentStep.techniqueId && ` (${currentStep.techniqueId})`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={unlinkAtomicTest}
                      className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-orange-900 text-orange-300 hover:bg-orange-800' : 'bg-orange-200 text-orange-700 hover:bg-orange-300'}`}
                    >
                      {t('playbooks.unlinkTest')}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.stepName')} *</label>
                  <input
                    type="text"
                    value={currentStep.name}
                    onChange={(e) => updateStep(activeStep, 'name', e.target.value)}
                    className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                    placeholder="Run Mimikatz"
                  />
                </div>

                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('common.description')}</label>
                  <textarea
                    value={currentStep.description || ''}
                    onChange={(e) => updateStep(activeStep, 'description', e.target.value)}
                    className={`w-full px-3 py-2 rounded h-16 border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.executorType')}</label>
                  <select
                    value={currentStep.executorType}
                    onChange={(e) => updateStep(activeStep, 'executorType', e.target.value)}
                    className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  >
                    {playbookService.EXECUTOR_TYPES.map((exec) => (
                      <option key={exec.value} value={exec.value}>{exec.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.timeout')}</label>
                  <input
                    type="number"
                    value={currentStep.timeout}
                    onChange={(e) => updateStep(activeStep, 'timeout', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                    min="1"
                    max="3600"
                  />
                </div>

                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.command')} *</label>
                  <textarea
                    value={currentStep.command}
                    onChange={(e) => updateStep(activeStep, 'command', e.target.value)}
                    className={`w-full px-3 py-2 rounded font-mono h-32 ${darkMode ? 'bg-gray-900 text-green-400' : 'bg-gray-100 text-green-700 border border-gray-300'}`}
                    placeholder="whoami /all"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {t('playbooks.variableHint')}
                  </p>
                </div>

                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.cleanupCommand')}</label>
                  <input
                    type="text"
                    value={currentStep.cleanupCommand || ''}
                    onChange={(e) => updateStep(activeStep, 'cleanupCommand', e.target.value)}
                    className={`w-full px-3 py-2 rounded font-mono border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                    placeholder="del /f /q C:\temp\payload.exe"
                  />
                </div>

                {/* MITRE Technique - Searchable Selector */}
                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.mitreTechnique')}</label>
                  <div className="flex gap-2">
                    <div
                      onClick={() => setShowMitreSelector(true)}
                      className={`flex-1 px-3 py-2 rounded border cursor-pointer flex items-center justify-between ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {currentStep.techniqueId ? (
                        <span>
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded mr-2 ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
                            {currentStep.techniqueId}
                          </span>
                          {currentStep.techniqueName}
                        </span>
                      ) : (
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          {t('playbooks.selectTechnique')}
                        </span>
                      )}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {currentStep.techniqueId && (
                      <button
                        onClick={() => updateStepMultiple(activeStep, { techniqueId: '', techniqueName: '', tactic: '' })}
                        className={`px-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'}`}
                        title="Clear"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Atomic Test Link */}
                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.atomicTest')}</label>
                  <button
                    onClick={openAtomicBrowser}
                    className={`w-full px-3 py-2 rounded border text-left flex items-center justify-between ${
                      darkMode ? 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {currentStep.atomicTestId ? (
                      <span className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-700'}`}>
                          Linked
                        </span>
                        <span className="truncate">{currentStep.atomicTestName}</span>
                      </span>
                    ) : (
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                        {t('playbooks.browseAtomicTests')}
                      </span>
                    )}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.delayAfter')}</label>
                  <input
                    type="number"
                    value={currentStep.delayAfter || 0}
                    onChange={(e) => updateStep(activeStep, 'delayAfter', parseInt(e.target.value))}
                    className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                    min="0"
                    max="300"
                  />
                </div>

                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>&nbsp;</label>
                  <div className="flex flex-wrap gap-3 pt-1">
                    <label className={`flex items-center gap-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={currentStep.requiresAdmin}
                        onChange={(e) => updateStep(activeStep, 'requiresAdmin', e.target.checked)}
                        className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                      />
                      {t('playbooks.requiresAdmin')}
                    </label>
                    <label className={`flex items-center gap-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={currentStep.continueOnError}
                        onChange={(e) => updateStep(activeStep, 'continueOnError', e.target.checked)}
                        className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                      />
                      {t('playbooks.continueOnError')}
                    </label>
                  </div>
                </div>

                <div className="col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.platforms')}</label>
                  <div className="flex flex-wrap gap-3">
                    {playbookService.PLATFORMS.map((platform) => (
                      <label key={platform.value} className={`flex items-center gap-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <input
                          type="checkbox"
                          checked={currentStep.platforms?.includes(platform.value)}
                          onChange={(e) => {
                            const platforms = e.target.checked
                              ? [...(currentStep.platforms || []), platform.value]
                              : currentStep.platforms?.filter(p => p !== platform.value);
                            updateStep(activeStep, 'platforms', platforms);
                          }}
                          className={`rounded ${darkMode ? 'bg-gray-700' : 'bg-white border-gray-300'}`}
                        />
                        {platform.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`rounded-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                {t('playbooks.noStepsDefined')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* MITRE Technique Selector Modal */}
      {showMitreSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowMitreSelector(false)}>
          <div className={`w-full max-w-5xl mx-4 rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('playbooks.browseMitre')}
              </h3>
              <button
                onClick={() => setShowMitreSelector(false)}
                className={`p-2 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <MitreTechniqueSelector
              onSelect={handleMitreSelect}
              selectedTechnique={currentStep?.techniqueId}
              onClose={() => setShowMitreSelector(false)}
              darkMode={darkMode}
            />
          </div>
        </div>
      )}

      {/* Atomic Test Browser Modal */}
      {showAtomicBrowser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowAtomicBrowser(false)}>
          <div className={`w-full max-w-4xl mx-4 rounded-lg overflow-hidden flex flex-col max-h-[85vh] ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('playbooks.browseAtomicTests')}
              </h3>
              <button
                onClick={() => setShowAtomicBrowser(false)}
                className={`p-2 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Search */}
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={atomicSearch}
                  onChange={(e) => setAtomicSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      searchAtomicTests(atomicSearch, atomicFilterTechnique);
                    }
                  }}
                  placeholder={t('playbooks.searchAtomicTests')}
                  className={`flex-1 px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                />
                <input
                  type="text"
                  value={atomicFilterTechnique}
                  onChange={(e) => setAtomicFilterTechnique(e.target.value)}
                  placeholder="T1059..."
                  className={`w-32 px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                />
                <button
                  onClick={() => searchAtomicTests(atomicSearch, atomicFilterTechnique)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  {t('common.search')}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {atomicLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : atomicTests.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('common.noResults')}
                </div>
              ) : (
                <div className="space-y-3">
                  {atomicTests.map((atomicTest) => (
                    <div key={atomicTest._id} className={`rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      {/* Technique header */}
                      <div className={`px-4 py-2 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
                            {atomicTest.techniqueId}
                          </span>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {atomicTest.name}
                          </span>
                          {atomicTest.tactic && (
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ({atomicTest.tactic})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Sub-tests */}
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {(atomicTest.atomicTests || []).map((subTest, idx) => (
                          <div
                            key={subTest.autoGeneratedGuid || idx}
                            className={`px-4 py-3 cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-blue-50'}`}
                            onClick={() => handleLinkAtomicTest(atomicTest, subTest)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h5 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {subTest.name}
                                </h5>
                                <p className={`text-xs mt-0.5 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {subTest.description}
                                </p>
                                <div className="flex items-center gap-3 mt-1.5">
                                  {subTest.executor?.type && (
                                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                      {subTest.executor.type}
                                    </span>
                                  )}
                                  {subTest.supportedPlatforms?.map(p => (
                                    <span key={p} className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{p}</span>
                                  ))}
                                  {subTest.executor?.elevationRequired && (
                                    <span className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>admin</span>
                                  )}
                                </div>
                              </div>
                              <button
                                className="ml-3 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLinkAtomicTest(atomicTest, subTest);
                                }}
                              >
                                {t('playbooks.linkTest')}
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* If no sub-tests, show the top-level test as linkable */}
                        {(!atomicTest.atomicTests || atomicTest.atomicTests.length === 0) && atomicTest.executorCommand && (
                          <div
                            className={`px-4 py-3 cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-blue-50'}`}
                            onClick={() => handleLinkAtomicTest(atomicTest, {
                              name: atomicTest.name,
                              description: atomicTest.description,
                              executor: { type: atomicTest.executorType, command: atomicTest.executorCommand },
                              supportedPlatforms: atomicTest.platforms
                            })}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h5 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {atomicTest.name}
                                </h5>
                                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {atomicTest.description}
                                </p>
                              </div>
                              <button className="ml-3 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex-shrink-0">
                                {t('playbooks.linkTest')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaybookBuilder;
